import {
  type ComputedRef,
  type InjectionKey,
  onMounted,
  onUnmounted,
  provide,
  ref,
  unref,
} from 'vue'

import type {
  CarouselContext,
  EmblaOptionsExtendedType,
} from '@shared/fondof-ui/components/molecules/carousel/types'
import type { EmblaPluginType } from 'embla-carousel'
import emblaCarouselVue from 'embla-carousel-vue'

export const CAROUSEL_CONTEXT_KEY: InjectionKey<CarouselContext> =
  Symbol('carouselContext')

export const useCarousel = (
  options: ComputedRef<EmblaOptionsExtendedType>,
  modules: EmblaPluginType[]
) => {
  const scrollProgress = ref(0)
  const scrollbarVisible = ref(false)

  const [emblaRef, emblaApi] = emblaCarouselVue(options, modules)

  const canScrollNext = ref<boolean>(false)
  const canScrollPrev = ref<boolean>(false)
  const selectedIndex = ref<number>(0)
  const scrollSnaps = ref<number[]>([])
  const prevNextButtons = ref<boolean>(options.value.prevNextButtons ?? false)
  const pagination = ref<boolean>(options.value.pagination ?? false)
  const scrollbar = ref<boolean>(options.value.scrollbar ?? false)
  const mobileScrollbar = ref<boolean>(options.value.mobileScrollbar ?? false)
  const axis = ref<'x' | 'y'>(options.value.axis ?? 'x')

  const updateScrollState = () => {
    if (!emblaApi.value) return

    const currentOptions = unref(options)

    canScrollNext.value = emblaApi.value.canScrollNext()
    canScrollPrev.value = emblaApi.value.canScrollPrev()
    selectedIndex.value = emblaApi.value.selectedScrollSnap()
    scrollSnaps.value = emblaApi.value?.scrollSnapList()

    prevNextButtons.value = !!currentOptions.prevNextButtons
    pagination.value = !!currentOptions.pagination
    scrollbar.value = !!currentOptions.scrollbar
    mobileScrollbar.value = !!currentOptions.mobileScrollbar
    axis.value = currentOptions.axis ?? 'x'

    updateScrollProgress()
    updateScrollbarVisibility()
  }

  const updateScrollbarVisibility = () => {
    if (!emblaApi.value) return

    scrollbarVisible.value =
      (emblaApi.value.scrollSnapList().length > 1 &&
        emblaApi.value.canScrollNext()) ||
      emblaApi.value.canScrollPrev()
  }

  const scrollToProgress = (progress: number) => {
    if (!emblaApi.value) return
    const { animation, limit, target, scrollProgress, scrollBody, scrollTo } =
      emblaApi.value.internalEngine()

    animation.stop()

    const currentProgress = scrollProgress.get(target.get())
    const allowedProgress = Math.min(Math.max(progress, 0), 1)
    const progressToTarget = allowedProgress - currentProgress
    const distance = progressToTarget * limit.length * -1

    scrollBody.useDuration(0)
    scrollTo.distance(distance, false)
  }

  const updateScrollProgress = () => {
    if (!emblaApi.value) return

    const progress = Math.max(0, Math.min(1, emblaApi.value.scrollProgress()))

    scrollProgress.value = progress
  }

  const scrollNext = () => {
    emblaApi.value?.scrollNext()
  }

  const scrollPrev = () => {
    emblaApi.value?.scrollPrev()
  }

  const scrollTo = (index: number) => {
    emblaApi.value?.scrollTo(index)
  }

  updateScrollState()

  onMounted(() => {
    if (!emblaRef.value) return

    scrollSnaps.value = emblaApi.value?.scrollSnapList() ?? []

    updateScrollState()

    emblaApi.value?.on('select', updateScrollState)
    emblaApi.value?.on('scroll', updateScrollProgress)

    emblaApi.value?.on('reInit', () => {
      updateScrollState()
    })
    emblaApi.value?.on('init', updateScrollState)
  })

  onUnmounted(() => {
    emblaApi.value?.destroy()
  })

  const context = {
    emblaRef,
    emblaApi,
    canScrollNext,
    canScrollPrev,
    selectedIndex,
    scrollSnaps,
    scrollNext,
    scrollPrev,
    scrollTo,
    prevNextButtons,
    pagination,
    scrollbar,
    scrollProgress,
    scrollbarVisible,
    scrollToProgress,
    mobileScrollbar,
    axis,
  }

  provide(CAROUSEL_CONTEXT_KEY, context)

  return context
}
